/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, jsx, keyframes } from '@compiled/react';

import type { SpinnerProps } from '@atlaskit/spinner/types';

export const presetSizes = {
	xsmall: 12,
	small: 16,
	medium: 24,
	large: 48,
	xlarge: 96,
};

const rotate = keyframes({
	to: { transform: 'rotate(360deg)' },
});

/**
 * There are three parts to the load in animation:
 * 1. Accelerated spin
 * 2. Stretch the spinner line
 */
const loadIn = keyframes({
	from: {
		transform: 'rotate(50deg)',
	},
	to: {
		transform: 'rotate(230deg)',
	},
});

const styles = cssMap({
	rotateStyles: {
		animationName: rotate,
		animationDuration: '0.86s',
		animationIterationCount: 'infinite',
		animationTimingFunction: 'cubic-bezier(0.4, 0.15, 0.6, 0.85)',
		transformOrigin: 'center',
	},
	loadInStyles: {
		animationName: loadIn,
		animationDuration: '1s',
		animationTimingFunction: 'ease-in-out',
		/**
		 * When the animation completes, stay at the last frame of the animation.
		 */
		animationFillMode: 'forwards',
	},
	wrapperStyles: {
		display: 'inline-flex',
		/**
		 * Align better inline with text.
		 */
		verticalAlign: 'middle',
	},
	circleStyles: {
		fill: 'none',
		strokeWidth: 1,
		strokeLinecap: 'butt',
		mixBlendMode: 'normal',
		'@media screen and (forced-colors: active)': {
			filter: 'grayscale(100%)',
			stroke: 'CanvasText',
		},
	},
});

/**
 *
 * A forked version of @atlaskit/spinner is an animated spinning icon that lets users know content is being loaded.
 *
 * - [Examples](https://atlassian.design/components/spinner/examples)
 * - [Code](https://atlassian.design/components/spinner/code)
 * - [Usage](https://atlassian.design/components/spinner/usage)
 */
const ColorfulSpinner: React.MemoExoticComponent<
	React.ForwardRefExoticComponent<SpinnerProps & React.RefAttributes<SVGSVGElement>>
> = React.memo(
	React.forwardRef<SVGSVGElement, SpinnerProps>(function Spinner(
		{ delay = 0, label, size: providedSize = 'medium', testId }: SpinnerProps,
		ref,
	) {
		const size: number =
			typeof providedSize === 'number' ? providedSize : presetSizes[providedSize];

		const animationDelay = `${delay}ms`;

		/**
		 * The Spinner animation uses a combination of two
		 * css animations on two separate elements.
		 */
		return (
			<span
				/**
				 * This span exists to off-load animations from the circle element,
				 * which were causing performance issues (style recalculations)
				 * on Safari and older versions of Chrome.
				 *
				 * This can be removed and styles placed back on the circle element once
				 * Safari fixes this bug and off-loads rendering to the GPU from the CPU.
				 */
				css={[styles.wrapperStyles, styles.rotateStyles]}
				data-testid={testId ? `${testId}-wrapper` : 'spinner-wrapper'}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ animationDelay, width: size, height: size }}
			>
				<svg
					height={size}
					width={size}
					viewBox="0 0 16 16"
					xmlns="http://www.w3.org/2000/svg"
					data-testid={testId}
					ref={ref}
					aria-label={label || undefined}
					css={styles.loadInStyles}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={{ animationDelay }}
					role={label ? 'img' : 'none'}
				>
					<circle
						cx="8"
						cy="8"
						r="7"
						css={styles.circleStyles}
						stroke={'#BF63F3'}
						strokeDasharray="2.75 41.25"
						strokeDashoffset="0"
					/>
					<circle
						cx="8"
						cy="8"
						r="7"
						css={styles.circleStyles}
						stroke={'#82B536'}
						strokeDasharray="2.75 41.25"
						strokeDashoffset="-2.75"
					/>
					<circle
						cx="8"
						cy="8"
						r="7"
						css={styles.circleStyles}
						stroke={'#1868DB'}
						strokeDasharray="2.75 41.25"
						strokeDashoffset="-5.5"
					/>
					<circle
						cx="8"
						cy="8"
						r="7"
						css={styles.circleStyles}
						stroke={'#FCA700'}
						strokeDasharray="2.75 41.25"
						strokeDashoffset="-8.25"
					/>
				</svg>
			</span>
		);
	}),
);

export default ColorfulSpinner;
